import {
  Component,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {
  GcInputFieldComponent,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import { InputFieldLabel } from '../../../../../shared/installation-report-content-v2/splicing-and-measuring-content/fiber-data/fiber-data-entites';
import { ContactInformation } from '../../../../entities/installation-reporting-mdu-details.entities';
import { FileMetaV2Response } from '../../model/installation-reporting-mdu-bff.entities';
import { InstallationReportSectionConfig } from '../../model/installation-reporting-mdu-section.types';
import { requiredItemsComplete } from '../../state/installation-reporting-mdu-section-state';
import { emptyItemAnswer } from '../../state/installation-reporting-mdu-section-state.mapper';
import {
  ItemAnswer,
  SectionNotApplicableChange,
  SectionState,
} from '../../state/installation-reporting-mdu-section-state.types';
import { InstallationReportFiberDataFormComponent } from '../installation-report-fiber-data-form/installation-report-fiber-data-form.component';
import { InstallationReportObjectHeaderComponent } from '../installation-report-object-header/installation-report-object-header.component';
import { BuildingSigningCardComponent } from '../building-signing-card/building-signing-card.component';
import { SectionSidebarComponent } from '../section-sidebar/section-sidebar.component';
import { SectionNotApplicableComponent } from '../section-not-applicable/section-not-applicable.component';
import { SelfInspectionSectionComponent } from '../self-inspection-section/self-inspection-section.component';
import { PortConnectionStatusContainerComponent } from '../../../../../shared/installation-report-content-v2/commissioning-content/port-connection-status-container.component/port-connection-status-container.component';

@Component({
  selector: 'app-installation-report-section-tab',
  imports: [
    InstallationReportObjectHeaderComponent,
    BuildingSigningCardComponent,
    SelfInspectionSectionComponent,
    InstallationReportFiberDataFormComponent,
    SectionSidebarComponent,
    SectionNotApplicableComponent,
    PortConnectionStatusContainerComponent,
    GcInputFieldComponent,
    IpoTranslatePipe,
  ],
  templateUrl: './installation-report-section-tab.component.html',
  styleUrl: './installation-report-section-tab.component.scss',
})
export class InstallationReportSectionTabComponent {
  config = input.required<InstallationReportSectionConfig>();
  address = input.required<string>();
  contact = input<ContactInformation | null>(null);
  accessId = input<string>();
  preInstallationMacAddress = input<string>();
  showConnectionStatus = input(false);
  canMarkNotApplicable = input(false);
  isNotApplicableSaving = input(false);
  state = model.required<SectionState>();
  mutationLoadingState = input<Record<string, boolean>>({});

  readonly isEnteringNotApplicableReason = signal(false);

  dateChanged = output<{
    field: 'plannedDate' | 'completedDate';
    value: Date | undefined;
  }>();
  notApplicableChanged = output<SectionNotApplicableChange>();
  selfInspectionCompleted = output<string>();
  selfInspectionMarkedNotApplicable = output<{
    itemId: string;
    comment: string;
  }>();
  selfInspectionUndone = output<string>();
  selfInspectionFilesSelected = output<{ itemId: string; files: File[] }>();
  selfInspectionAttachmentsViewed = output<{
    itemId: string;
    attachments: FileMetaV2Response[];
  }>();
  signingStarted = output<void>();
  navigateToPreinstallation = output<void>();

  hasLeftColumn = computed<boolean>(() => {
    const config = this.config();
    return config.hasSigning || config.items.length > 0 || config.hasFiberData;
  });

  isReadyForSigning = computed<boolean>(() => {
    const config = this.config();
    const state = this.state();
    return (
      config.hasSigning &&
      requiredItemsComplete(config, state) &&
      !!state.completedDate
    );
  });

  showSectionContent = computed<boolean>(
    () => !this.state().notApplicable && !this.isEnteringNotApplicableReason(),
  );

  private updateState(partial: Partial<SectionState>): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }

  onShowControlled(showControlled: boolean): void {
    this.updateState({ showControlled });
  }

  onMarkDone(itemId: string): void {
    this.patchItem(itemId, { completed: true });
    this.selfInspectionCompleted.emit(itemId);
  }

  onMarkNotApplicable(event: { itemId: string; comment: string }): void {
    this.patchItem(event.itemId, {
      completed: true,
      isNotCompletable: true,
      isNotCompletableComment: event.comment,
    });
    this.selfInspectionMarkedNotApplicable.emit(event);
  }

  onUploadFiles(event: { itemId: string; files: File[] }): void {
    this.selfInspectionFilesSelected.emit(event);
  }

  onUndo(itemId: string): void {
    this.patchItem(itemId, { completed: false, files: [] });
    this.selfInspectionUndone.emit(itemId);
  }

  onPlannedDate(date?: Date): void {
    this.updateState({ plannedDate: date });
    this.dateChanged.emit({ field: 'plannedDate', value: date });
  }

  onCompletedDate(date?: Date): void {
    this.updateState({ completedDate: date });
    this.dateChanged.emit({ field: 'completedDate', value: date });
  }
  onComment(comment: string): void {
    this.updateState({ comment });
  }

  onChecklist(event: { field: string; checked: boolean }): void {
    this.updateState({
      imageChecklist: {
        ...this.state().imageChecklist,
        [event.field]: event.checked,
      },
    });
  }

  onFiles(files: File[]): void {
    this.updateState({ uploadedFiles: files });
  }

  onPanelcardChanged(usesPanelCard: boolean): void {
    this.updateState({ usesPanelCard });
  }

  onFiberField(event: { label: InputFieldLabel; value: string }): void {
    this.updateState({
      fiberData: {
        ...this.state().fiberData,
        [event.label]: event.value,
      },
    });
  }

  onMacAddress(macAddress: string): void {
    this.updateState({ macAddress });
  }

  private patchItem(itemId: string, patch: Partial<ItemAnswer>): void {
    const current = this.state().itemAnswers[itemId] ?? emptyItemAnswer();
    this.updateState({
      itemAnswers: {
        ...this.state().itemAnswers,
        [itemId]: { ...current, ...patch },
      },
    });
  }
}
